import React from 'react';
import { Button, Divider, Drawer, Radio, Switch } from 'antd';
import { HolderOutlined } from '@ant-design/icons';
import { DragDropContext, Droppable, Draggable } from '@hello-pangea/dnd';

/**
 * Column configuration drawer — toggle visibility and drag-to-reorder columns.
 *
 * @param {Object} props
 * @param {boolean} props.open - Drawer open state
 * @param {Function} props.onClose - Close handler
 * @param {Array} props.columns - All columns in current order (from useColumnConfig.orderedAllColumns)
 * @param {Set} props.hidden - Set of hidden column keys
 * @param {Array} props.fixedKeys - Column keys that can't be hidden/reordered
 * @param {Function} props.onToggle - (key) => void
 * @param {Function} props.onMove - (fromIndex, toIndex) => void
 * @param {Function} props.onReset - () => void
 */
const PAGE_SIZE_OPTIONS = [10, 15, 25, 50, 100];

export default function ColumnConfigDrawer({
  open,
  onClose,
  columns,
  hidden,
  fixedKeys,
  onToggle,
  onMove,
  onReset,
  pageSize,
  onPageSizeChange,
  hidePageSize,
}) {
  const handleDragEnd = (result) => {
    if (!result.destination) return;
    const fromIndex = result.source.index;
    const toIndex = result.destination.index;
    if (fromIndex === toIndex) return;
    onMove(fromIndex, toIndex);
  };

  return (
    <Drawer
      title="Settings"
      placement="right"
      onClose={onClose}
      open={open}
      width={320}
      zIndex={1010}
      footer={
        <div style={{ display: 'flex', justifyContent: 'flex-end' }}>
          <Button onClick={onReset}>Reset to defaults</Button>
        </div>
      }
    >
      {!hidePageSize && (
        <>
          <div>
            <label style={{ display: 'block', marginBottom: 8, fontWeight: 500 }}>Page size</label>
            <Radio.Group value={pageSize} onChange={(e) => onPageSizeChange(e.target.value)} size="small">
              {PAGE_SIZE_OPTIONS.map((size) => (
                <Radio.Button key={size} value={size}>
                  {size}
                </Radio.Button>
              ))}
            </Radio.Group>
          </div>
          <Divider style={{ margin: '16px 0 8px' }} />
        </>
      )}
      <label style={{ display: 'block', marginBottom: 8, fontWeight: 500 }}>Columns</label>
      <DragDropContext onDragEnd={handleDragEnd}>
        <Droppable droppableId="column-config">
          {(provided) => (
            <div
              ref={provided.innerRef}
              {...provided.droppableProps}
              style={{ display: 'flex', flexDirection: 'column' }}
            >
              {columns.map((col, index) => {
                const key = col.key;
                const isFixed = fixedKeys.includes(key);
                const isHidden = hidden.has(key);

                return (
                  <Draggable key={key} draggableId={key} index={index} isDragDisabled={isFixed}>
                    {(provided, snapshot) => (
                      <div
                        ref={provided.innerRef}
                        {...provided.draggableProps}
                        style={{
                          display: 'flex',
                          alignItems: 'center',
                          gap: 10,
                          padding: '8px 4px',
                          borderRadius: 4,
                          backgroundColor: snapshot.isDragging ? '#e6f4ff' : 'transparent',
                          borderBottom: '1px solid #f0f0f0',
                          cursor: isFixed ? 'default' : 'grab',
                          ...provided.draggableProps.style,
                        }}
                      >
                        <span
                          {...provided.dragHandleProps}
                          style={{ color: isFixed ? '#d9d9d9' : '#999', cursor: isFixed ? 'default' : 'grab' }}
                        >
                          <HolderOutlined />
                        </span>
                        <span style={{ flex: 1, fontSize: 14, color: isHidden ? '#bfbfbf' : '#333' }}>
                          {typeof col.title === 'string' || !col.title ? col.title || key : key}
                        </span>
                        <Switch size="small" checked={!isHidden} disabled={isFixed} onChange={() => onToggle(key)} />
                      </div>
                    )}
                  </Draggable>
                );
              })}
              {provided.placeholder}
            </div>
          )}
        </Droppable>
      </DragDropContext>
    </Drawer>
  );
}
